import { ArrowLeft, ArrowRight, Package, RotateCcw, ShieldCheck, Users2 } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { normalizeInternalRole, type InternalRole } from "@/lib/roles";
import { CREDIT_KEY_SCREENS } from "./credit-key-screens";
import { ProductScreenshot } from "./ProductScreenshot";
import { AppPageLink } from "./AppPageLink";
import { APP_PAGES, type AppPageDestination } from "./app-pages";

type CreditKeyAudience = "admin" | "sales" | "warehouse";

const ROLE_GUIDES: Record<InternalRole, CreditKeyAudience | null> = {
	admin: "admin",
	sales: "sales",
	warehouse: "warehouse",
	logistics: "warehouse",
	accounting: null,
	inventory: null,
};

export function getCreditKeyGuideForRole(userRole: string | undefined): CreditKeyAudience | null {
	const role = normalizeInternalRole(userRole);
	return role ? ROLE_GUIDES[role] : null;
}

const GUIDE_OPTIONS = [
	{ value: "admin", label: "Admin", accessibleLabel: "For admins", icon: ShieldCheck },
	{ value: "sales", label: "Sales", accessibleLabel: "For sales", icon: Users2 },
	{ value: "warehouse", label: "Warehouse", accessibleLabel: "For warehouse", icon: Package },
] as const;

const GUIDE_INTROS: Record<CreditKeyAudience, string> = {
	admin: "Manage account access in Cherry. The screenshots show an example account.",
	sales: "Help a customer set up Credit Key on the customer website. The screenshot shows an example account.",
	warehouse: "Follow the storage steps in Cherry. The screenshots show an example order.",
};
type ScreenName = keyof typeof CREDIT_KEY_SCREENS;
type GuideStep = {
	screen: ScreenName;
	actor: string;
	location: string;
	title: string;
	description: React.ReactNode;
	alt: string;
	destination: AppPageDestination;
};

const GUIDES: Record<CreditKeyAudience, readonly GuideStep[]> = {
	admin: [
		{
			screen: "admin-users",
			destination: APP_PAGES.adminUsers,
			actor: "Administrator action",
			location: "Cherry → Admin → Users",
			title: "Find the customer’s account.",
			description: <>If the customer cannot see Credit Key, find their account in <strong>Users</strong> and select <strong>Credit Key access</strong> beneath their name.</>,
			alt: "Cherry Users table with the Credit Key access button beneath an example customer",
		},
		{
			screen: "admin-dialog",
			destination: { ...APP_PAGES.adminUsers, hint: "Choose a customer, then select Credit Key access to open this dialog." },
			actor: "Administrator action",
			location: "Cherry → Credit Key access dialog",
			title: "Enable Credit Key for the account.",
			description: <>Add an optional reason, then choose <strong>Enable access</strong>. This controls whether the account can use Credit Key; financing approval is handled separately by Credit Key.</>,
			alt: "Cherry Credit Key access dialog with the optional reason field and Enable access button",
		},
	],
	sales: [
		{
			screen: "customer-setup",
			destination: APP_PAGES.customerPayment,
			actor: "Customer action",
			location: "Customer website → Dashboard → Payment Methods",
			title: "Help the customer start their setup.",
			description: <>The customer signs in, opens <strong>Payment Methods</strong>, and selects <strong>Set up Credit Key</strong>. If they already started, it says <strong>Resume Credit Key setup</strong>. The customer completes the application with Credit Key. If Credit Key is missing, ask an administrator to enable access for the account.</>,
			alt: "Customer Payment Methods Credit Key card with the Set up Credit Key button",
		},
	],
	warehouse: [
		{
			screen: "warehouse-overview",
			destination: APP_PAGES.storageOrders,
			actor: "Warehouse action",
			location: "Cherry → Logistics → Orders → Order details",
			title: "Print and attach the customer’s bag tags.",
			description: <>Find the order’s <strong>Customer storage</strong> panel. Choose <strong>Print bag tags</strong> and attach the customer and purchase order tag to every bag. A <strong>Holding DO</strong> is not an instruction to ship.</>,
			alt: "Cherry Customer storage panel with Holding DO, Print bag tags, and Assign locations controls",
		},
		{
			screen: "warehouse-location",
			destination: { ...APP_PAGES.storageOrders, hint: "Choose the order’s Customer storage panel, then select Assign locations." },
			actor: "Warehouse action",
			location: "Cherry → Assign locations → Store customer coffee",
			title: "Assign the bags to their pallet location.",
			description: <>Choose <strong>Assign locations</strong> to open this dialog. Select the location under <strong>Store for customer · location 1</strong> and enter the whole bag count. Use <strong>Add pallet location</strong> when the bags are split across locations.</>,
			alt: "Cherry Store customer coffee dialog with Choose location and Bags fields",
		},
		{
			screen: "warehouse-save",
			destination: { ...APP_PAGES.storageOrders, hint: "Choose the order and open Assign locations before confirming the tags." },
			actor: "Warehouse action",
			location: "Cherry → Store customer coffee",
			title: "Confirm the tags and save the locations.",
			description: <>After tagging every bag, tick <strong>Every bag has the customer and purchase order tag</strong>. Review the location and bag count, then choose <strong>Save locations</strong>.</>,
			alt: "Cherry placement dialog with a selected pallet, confirmed bag tags, and Save locations button",
		},
		{
			screen: "warehouse-ready",
			destination: APP_PAGES.storageOrders,
			actor: "Warehouse action",
			location: "Cherry → Order details → Customer storage",
			title: "Mark the coffee stored.",
			description: <>Back in <strong>Customer storage</strong>, check the recorded location, bag count, and <strong>Tagged</strong> status. Choose <strong>Mark stored</strong>, then look for <strong>Stored for Customer</strong>. Pick these bags only against a separate release DO.</>,
			alt: "Cherry Customer storage panel with saved pallet locations, Tagged status, and enabled Mark stored button",
		},
	],
};

type Viewer = { userRole: string | undefined; userEmail?: string | null; isLive: boolean };

function GuideContent({ audience, ...viewer }: { audience: CreditKeyAudience } & Viewer) {
	const [index, setIndex] = React.useState(0);
	const steps = GUIDES[audience];
	const step = steps[index];
	if (!step) throw new Error("Unknown Credit Key guide step");
	const screen = CREDIT_KEY_SCREENS[step.screen];
	const titleId = React.useId();
	const last = index === steps.length - 1;

	React.useEffect(() => {
		for (const item of steps) {
			const image = new Image();
			image.src = CREDIT_KEY_SCREENS[item.screen].src;
		}
	}, [steps]);

	return (
		<section className="mt-5 overflow-hidden rounded-xl border border-border" aria-labelledby={titleId}>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
				<p className="text-xs font-medium tabular-nums" role="status">Step {index + 1} of {steps.length}</p>
				{steps.length > 1 && <nav aria-label="Walkthrough navigation" className="flex w-full gap-2 sm:w-auto">
					<Button type="button" aria-label="Previous step" variant="outline" className="min-h-11 min-w-0 flex-1 px-2 text-xs has-[>svg]:px-2 sm:flex-none sm:px-3 sm:text-sm sm:has-[>svg]:px-3" disabled={index === 0} onClick={() => setIndex(index - 1)}>
						<ArrowLeft className="size-4" aria-hidden="true" /><span>Previous<span className="hidden sm:inline"> step</span></span>
					</Button>
					<Button type="button" aria-label={last ? "Restart guide" : "Next step"} className="min-h-11 min-w-0 flex-1 px-2 text-xs has-[>svg]:px-2 sm:flex-none sm:px-3 sm:text-sm sm:has-[>svg]:px-3" onClick={() => setIndex(last ? 0 : index + 1)}>
						{last ? <span>Restart<span className="hidden sm:inline"> guide</span></span> : "Next step"}
						{last ? <RotateCcw className="size-4" aria-hidden="true" /> : <ArrowRight className="size-4" aria-hidden="true" />}
					</Button>
				</nav>}
			</div>
			<div className="space-y-3 px-4 py-5 sm:px-5">
				<p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{step.actor}</p>
				<h3 id={titleId} aria-live="polite" className="text-[17px] font-medium leading-6 tracking-tight">{step.title}</h3>
				<p className="text-sm leading-7 text-muted-foreground [&_strong]:font-medium [&_strong]:text-foreground">{step.description}</p>
				<AppPageLink destination={step.destination} {...viewer} />
			</div>
			<ProductScreenshot key={step.screen} screen={screen} alt={step.alt} location={step.location} caption="Example account shown." />
		</section>
	);
}

function StaffGuide({ audienceForRole, ...viewer }: { audienceForRole: CreditKeyAudience } & Viewer) {
	const [audience, setAudience] = React.useState(audienceForRole);
	const canChoose = audienceForRole === "admin";
	return (
		<div aria-label="Credit Key step-by-step guide">
			{canChoose && <>
				<p className="mb-3 text-xs font-medium">Choose a guide</p>
				<div role="group" aria-label="Choose a staff guide" className="inline-grid w-full grid-cols-3 gap-1 rounded-[14px] border border-border bg-muted p-1 sm:w-auto">
					{GUIDE_OPTIONS.map(({ value, label, accessibleLabel, icon: Icon }) => (
						<Button key={value} type="button" aria-label={accessibleLabel} variant={audience === value ? "outline" : "ghost"} className="h-auto min-h-11 min-w-0 whitespace-normal px-2 text-xs aria-pressed:shadow-sm sm:px-4 sm:text-sm" aria-pressed={audience === value} onClick={() => setAudience(value)}>
							<Icon className="hidden size-4 sm:block" aria-hidden="true" />
							{label}
						</Button>
					))}
				</div>
			</>}
			<p className="mt-3 text-xs leading-6 text-muted-foreground">{GUIDE_INTROS[audience]}</p>
			<GuideContent key={audience} audience={audience} {...viewer} />
		</div>
	);
}

export function CreditKeyWalkthrough({ userRole, ...viewer }: Viewer) {
	const audienceForRole = getCreditKeyGuideForRole(userRole);
	if (!audienceForRole) return null;
	return <StaffGuide key={audienceForRole} audienceForRole={audienceForRole} userRole={userRole} {...viewer} />;
}
